/**
 * SwitchPlanCard — self-serve plan-switch UI for active subscribers.
 * Talks to POST /api/subscribe/switch (no proration, T&C required).
 *
 * Covers all four paths:
 *   • Starter → Platinum          (upgrade)
 *   • Platinum → Starter          (downgrade)
 *   • Pilot   → Platinum now      (convert immediately)
 *   • Pilot   → Starter           (end pilot, move to Starter)
 */
import React, { useEffect, useState } from "react";
import { ArrowRightLeft, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

export default function SwitchPlanCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    api.switchOptions()
      .then(d => { if (alive) { setData(d); setLoading(false); } })
      .catch(e => { if (alive) { setErr(e?.response?.data?.detail || e.message || "Failed to load"); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const doSwitch = async () => {
    if (!selected || !accepted) return;
    setBusy(true); setErr("");
    try {
      const r = await api.switchPlan({
        target_plan: selected.target_plan,
        mode: selected.mode,
        accepted_terms: true,
        accepted_terms_version: data.terms_version,
      });
      setResult(r);
      // Refresh after a moment so the banner reflects new plan.
      setTimeout(() => window.location.reload(), 2400);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Switch failed");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="card p-5" data-testid="switch-plan-loading">
        <div className="text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading plan options…
        </div>
      </div>
    );
  }

  if (!data || !data.current_plan) {
    return (
      <div className="card p-5" data-testid="switch-plan-no-sub">
        {err && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-3" data-testid="switch-error">
            {err}
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <AlertCircle size={14} /> You don&apos;t have an active subscription — nothing to switch. Head to <Link to="/pricing" className="underline">pricing</Link> to start.
        </div>
      </div>
    );
  }

  if (result?.ok) {
    return (
      <div className="card p-5 border-emerald-200 bg-emerald-50" data-testid="switch-plan-success">
        <div className="flex items-start gap-3">
          <CheckCircle2 size={18} className="text-emerald-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-display text-base font-bold text-emerald-900">Plan switched</div>
            <div className="text-sm text-emerald-800 mt-1">{result.message}</div>
            <div className="text-[11px] font-mono text-emerald-700 mt-2">Terms {result.terms_version} accepted · reloading…</div>
          </div>
        </div>
      </div>
    );
  }

  const options = data.options || [];
  const planLabel = data.in_pilot ? "14-day Platinum pilot" :
                    data.current_plan === "pro" ? "Harmonizer Platinum · $350/mo" :
                    data.current_plan === "starter" ? "Harmonizer Starter · $200/mo" :
                    data.current_plan;

  return (
    <div className="card p-6" data-testid="switch-plan-card">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">Manage subscription</div>
          <div className="font-display text-lg font-bold text-zinc-900 mt-1 flex items-center gap-2">
            <ArrowRightLeft size={16} className="text-violet-600" />
            Switch plan
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Current plan</div>
          <div className="text-sm font-semibold text-zinc-900" data-testid="switch-current-plan">{planLabel}</div>
        </div>
      </div>

      {options.length === 0 ? (
        <div className="text-sm text-zinc-500 py-4" data-testid="switch-no-options">
          You&apos;re already on the best fit for your current state. Contact <a href="mailto:Requests@Capittance.com" className="underline">Requests@Capittance.com</a> if you&apos;d like something custom.
        </div>
      ) : (
        <>
          <div className="grid gap-3 mb-4">
            {options.map((o) => {
              const chosen = selected?.mode === o.mode;
              return (
                <label
                  key={o.mode}
                  className={`flex gap-3 items-start p-4 rounded-lg border cursor-pointer transition-colors ${
                    chosen ? "border-violet-500 bg-violet-50/40" : "border-zinc-200 hover:border-zinc-300"
                  }`}
                  data-testid={`switch-option-${o.mode}`}
                >
                  <input
                    type="radio"
                    name="switch-option"
                    className="mt-1 accent-violet-600"
                    checked={chosen}
                    onChange={() => setSelected(o)}
                    data-testid={`switch-option-radio-${o.mode}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-semibold text-zinc-900">{o.label}</div>
                      <div className="font-mono text-xs text-violet-700">{o.price}</div>
                    </div>
                    <div className="text-[13px] text-zinc-600 mt-1">{o.description}</div>
                  </div>
                </label>
              );
            })}
          </div>

          {/* T&C acceptance */}
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="switch-terms-label">
              <input
                type="checkbox"
                className="mt-1 accent-amber-600"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                data-testid="switch-terms-checkbox"
              />
              <div className="text-sm text-zinc-800 leading-relaxed">
                I&apos;ve read and agree to the current{" "}
                <Link to="/terms" target="_blank" className="underline text-amber-800 font-semibold" data-testid="switch-terms-link">
                  Terms &amp; Conditions (v{data.terms_version})
                </Link>{" "}
                and understand this switch is <b>not prorated</b> — no credit or refund is issued for the remainder of my current billing period.
              </div>
            </label>
          </div>

          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-4" data-testid="switch-error">
              {err}
            </div>
          )}

          <button
            onClick={doSwitch}
            disabled={!selected || !accepted || busy}
            className="btn btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="switch-confirm-btn"
          >
            {busy ? <><Loader2 size={14} className="animate-spin" /> Switching…</> : (
              <>Confirm switch{selected ? ` — ${selected.label}` : ""}</>
            )}
          </button>
        </>
      )}
    </div>
  );
}
